<script setup lang="ts">
/**
 * MercariProductList
 *
 * A component for displaying a list of Mercari products with various states:
 * - Loading state with skeleton
 * - Product list with load more
 * - Empty state
 * - Error state with retry
 *
 * @example Basic Usage
 * ```vue
 * <MercariProductList
 *   ref="productListRef"
 *   :keyword="searchQuery"
 *   :shop-id="shopId"
 *   :location-id="locationId"
 *   :item-content="productItem"
 *   :load-more-button-text="loadMoreButtonText"
 *   :edge-case="edgeCase"
 *   @fetch-complete="handleFetchComplete"
 *   @item-click="handleItemClick"
 * />
 * ```
 *
 * @props
 * - keyword: Search keyword for products
 * - shopId: Shop ID for shop mode
 * - locationId: Page GA tracking，（ex: MercariCampaign、MercariIndex）
 * - itemContent: Content configuration for product items
 * - loadMoreButtonText: Text for load more button
 * - edgeCase: Configuration for empty and error states
 * - viewMoreMode: Mode for handling "View More" button
 *   - 'load-more': Show button when there's more data, clicking loads more data (default)
 *   - 'redirect': Always show button, clicking emits view-more-click event for redirect
 *   - 'none': Don't show any button
 *
 * @events
 * - fetch-complete: Emitted when fetch completes (success or failure)
 *   ```ts
 *   interface FetchCompleteEvent {
 *     success: boolean
 *     productList: IMercariProductListItem[]
 *     additionalProducts: IMercariProductListItem[]
 *   }
 *   ```
 * - item-click: Emitted when product item is clicked
 *   ```ts
 *   interface ItemClickEvent {
 *     item: IMercariProductListItem
 *     targetUrl: string
 *     index: number
 *   }
 *   ```
 * - view-more-click: Emitted when "View More" button is clicked in redirect mode
 *   ```ts
 *   interface ViewMoreClickEvent {
 *     keyword: string
 *   }
 *   ```
 *
 *
 * @state
 * ```ts
 * interface State {
 *   page: number
 *   productList: IMercariProductListItem[]
 *   status: 'loading' | 'error' | 'success'
 *   hasNextPage: boolean
 * }
 * ```
 */

import { computed, onMounted, type PropType, reactive, ref } from 'vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import {
	fetchMercariProductList,
	fetchSettingInsurance,
	fetchSettingWarehouses,
	fetchSettingSupportedCurrencies,
	instanceProductSharing,
} from '@/assets/ts/apiManagement'
import MercariProductListItem from '@/components/MercariProductListItem.vue'
import type { IMercariProductListItemContent } from '@/components/MercariProductListItem.vue'
import type { IMercariProductListItem } from '@/types/Mercari'
import { getVueAssetImagePath, useBreakpoints } from '@/lib/utils'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { formatTrackingKeyword } from '@/types/Mercari'
import {
	exchangeLocalPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import {
	getSortingMethodLabel,
	getCategoryLevels,
	getFilterTrackingValues,
	MERCARI_CONSTANTS,
	isValidPrice,
	isValidPriceRange,
} from '@/lib/mercariFilterConfig'

export interface IMercariProductListFetchCompleteEvent {
	success: boolean
	productList: IMercariProductListItem[]
	additionalProducts: IMercariProductListItem[]
	searchKeyword?: string
}

export interface IMercariProductListItemClickEvent {
	item: IMercariProductListItem
	targetUrl: string
	index: number
}

export interface IMercariProductListEdgeCase {
	empty: {
		title?: string
		description: string
	}
	serverError: {
		title: string
		description: string
		buttonText: string
	}
}

const props = defineProps({
	viewMoreMode: {
		type: String,
		default: 'load-more',
		validator: (value: string) =>
			['load-more', 'redirect', 'none'].includes(value),
	},
	keyword: {
		type: String,
		default: '',
	},
	itemContent: {
		type: Object as PropType<IMercariProductListItemContent>,
	},
	loadMoreButtonText: {
		type: String,
		default: 'View more',
	},
	edgeCase: {
		type: Object as PropType<IMercariProductListEdgeCase>,
		default: () => ({
			empty: {
				description: 'No results found, Try using different keywords.',
			},
			serverError: {
				title: 'Server is busy',
				description: 'Please try again in a few minutes.',
				buttonText: 'Try again',
			},
		}),
	},
	shopId: {
		type: String,
		default: '',
	},
	locationId: {
		type: String,
		default: GaGeneralPageName.MercariIndex,
	},
	type: {
		type: String,
		default: 'mercari',
		validator: (value: string) =>
			[
				'mercari',
				'buyaholic-filter',
				'buyaholic-search',
				'buyaholic-user',
			].includes(value),
	},
	// buyaholic-case
	warehouses: {
		type: String,
		default: '',
	},
	// buyaholic-case
	interests: {
		type: String,
		default: '',
	},
	// buyaholic-case
	sort: {
		type: String,
		default: '',
	},
	// buyaholic-case
	username: {
		type: String,
		default: '',
	},
	filterState: {
		type: Object,
		default: () => ({}),
	},
	isTranslated: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits<{
	'fetch-complete': [IMercariProductListFetchCompleteEvent]
	'item-click': [IMercariProductListItemClickEvent]
	'keyword-used': [string]
	'view-more-click': [string]
}>()

const warehouseIdMap = ref<Record<string, { name: string; currency: string }>>(
	{},
)
const supportedCurrencies = ref<Record<string, { symbol: string }>>({})
const rates = ref<Record<string, number>>({})

type DisplayState = 'LOADING' | 'LIST' | 'EMPTY' | 'ERROR'

const API_STATUS = {
	LOADING: 'loading',
	ERROR: 'error',
	SUCCESS: 'success',
} as const

type APIStatus = (typeof API_STATUS)[keyof typeof API_STATUS]

/**
 * Type of product list fetch operation
 * - `NEW_SEARCH`: Reset and start a new search
 * - `LOAD_MORE`: Load next page of existing search
 * @remarks
 * NEW_SEARCH behavior:
 * - reset page to 0
 * - clear existing product list
 * - set loading state
 * - replace existing list
 *
 * LOAD_MORE behavior:
 * - use current page
 * - keep existing product list
 * - set loadingMore state
 * - append new data to the list
 */

interface State {
	page: number
	productList: IMercariProductListItem[]
	status: APIStatus
	hasNextPage: boolean
	lastFetchedIndex: number
	remainingProducts: IMercariProductListItem[]
}

const { isSmall, isTabletPortraitScreen, isTabletLandscapeScreen } =
	useBreakpoints()

const ITEMS_PER_PAGE = computed(() => {
	type PageConfig = {
		small: number
		tabletPortrait: number
		tabletLandscape: number
		desktop: number
	}

	type ItemsConfigType = {
		[key: string]: PageConfig
	}

	const itemsConfig: ItemsConfigType = {
		[GaGeneralPageName.MercariCampaign]: {
			small: 8,
			tabletPortrait: 9,
			tabletLandscape: 8,
			desktop: 10,
		},
		default: {
			small: 10,
			tabletPortrait: 12,
			tabletLandscape: 16,
			desktop: 20,
		},
	}

	const pageConfig = itemsConfig[props.locationId] || itemsConfig.default

	if (isSmall.value) return pageConfig.small
	if (isTabletPortraitScreen.value) return pageConfig.tabletPortrait
	if (isTabletLandscapeScreen.value) return pageConfig.tabletLandscape
	return pageConfig.desktop
})

const isShopMode = computed(() => !!props.shopId && props.shopId.trim() !== '')

const isBuyaholicMode = computed(() => {
	return (
		props.type !== 'mercari' &&
		['buyaholic-filter', 'buyaholic-search', 'buyaholic-user'].includes(
			props.type,
		)
	)
})

const state = reactive<State>({
	page: 0,
	productList: [],
	status: API_STATUS.LOADING,
	hasNextPage: false,
	/**
	 * When "View More" is clicked, the next batch of listings should start
	 * from the index of the last item in the previous batch.
	 * For example, if the last visible listing was index 20,
	 *the first listing in the new batch should start from index 21.
	 */
	lastFetchedIndex: 0,
	remainingProducts: [],
})

const isLoadingMore = computed(() => {
	return state.status === API_STATUS.LOADING && state.productList.length > 0
})

const currentDisplay = computed<DisplayState>(() => {
	if (state.status === API_STATUS.ERROR) return 'ERROR'
	if (state.status === API_STATUS.SUCCESS && state.productList.length === 0)
		return 'EMPTY'
	if (state.productList.length > 0) return 'LIST'
	return 'LOADING'
})

const shouldShowViewMoreButton = computed(() => {
	if (props.viewMoreMode === 'none') return false

	if (props.viewMoreMode === 'load-more') {
		return state.hasNextPage && state.status === API_STATUS.SUCCESS
	}

	return true
})

const currentViewMoreLink = computed(() => {
	if (props.viewMoreMode === 'none' || props.viewMoreMode === 'redirect')
		return ''
	const url = new URL(window.location.href)
	url.searchParams.set('page', state.page.toString())
	return url.toString()
})

const handleItemClick = (item: IMercariProductListItem, targetUrl: string) => {
	emit('item-click', {
		item,
		targetUrl,
		index: state.productList.findIndex(product => product.id === item.id),
	})
}

const handleLoadMore = () => {
	fetchProducts({}, true)
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: props.locationId,
		button_name: 'View More Listing',
	})
}

/**
 * View More button click event
 * based on viewMoreMode property, different behaviors:
 * - 'load-more'：call handleLoadMore to load more products
 * - 'redirect'：trigger view-more-click event, let parent component handle redirect logic
 * - 'none'：display no button
 */
const handleViewMoreClick = () => {
	if (props.viewMoreMode === 'load-more') {
		// load more mode: get next page data
		handleLoadMore()
	} else {
		// redirect mode: trigger event to let parent component handle redirect logic
		if (isShopMode.value) {
			emit('view-more-click', props.shopId)
		}
		emit('view-more-click', props.keyword)
	}
}

const emitFetchComplete = (
	success: boolean,
	products: IMercariProductListItem[],
	searchKeyword?: string,
) => {
	emit('fetch-complete', {
		success,
		productList: state.productList,
		additionalProducts: products,
		searchKeyword,
	})
}

const filterDuplicateProducts = (newProducts: IMercariProductListItem[]) => {
	const existingIds = new Set(state.productList.map(product => product.id))
	return newProducts.filter(product => !existingIds.has(product.id))
}

const buildFilterParams = () => {
	const filterParams: Record<string, any> = {}

	if (!props.filterState) return filterParams

	// Category
	if (props.filterState.category && props.filterState.category !== 'all') {
		filterParams.categoryId = props.filterState.category
	}

	// for page init loading
	if (!filterParams.categoryId) {
		const query = new URLSearchParams(window.location.search)
		let categoryId = query.get('category_id')
		if (categoryId) {
			categoryId = categoryId.replace(/\/+/g, '')
			filterParams.categoryId = categoryId
		}
	}

	// Item Condition
	if (props.filterState.itemCondition?.length > 0) {
		const itemConditionValues = props.filterState.itemCondition.map(
			(id: string) =>
				MERCARI_CONSTANTS.CONDITION_MAP[
					id as keyof typeof MERCARI_CONSTANTS.CONDITION_MAP
				] || id,
		)
		filterParams.itemConditionId = itemConditionValues.join(',')
	}

	// Status
	if (props.filterState.status?.length > 0) {
		// FIXME:BC-9393
		const statusValues = [...props.filterState.status]
		if (
			statusValues.includes('sold_out') &&
			!statusValues.includes('trading')
		) {
			statusValues.push('trading')
		}
		filterParams.status = statusValues.join(',')
	}

	// Price
	if (props.filterState.price) {
		// Price handling: if any non-empty price field is invalid, ignore both prices
		const hasInvalidPrice =
			(props.filterState.price.min !== '' &&
				props.filterState.price.min &&
				!isValidPrice(props.filterState.price.min)) ||
			(props.filterState.price.max !== '' &&
				props.filterState.price.max &&
				!isValidPrice(props.filterState.price.max))

		if (!hasInvalidPrice) {
			if (
				props.filterState.price.min &&
				isValidPrice(props.filterState.price.min)
			) {
				filterParams.priceMin = props.filterState.price.min
			}
			if (
				props.filterState.price.max &&
				isValidPrice(props.filterState.price.max)
			) {
				filterParams.priceMax = props.filterState.price.max
			}

			const hasBothPrices = filterParams.priceMin && filterParams.priceMax
			if (
				hasBothPrices &&
				!isValidPriceRange(filterParams.priceMin, filterParams.priceMax)
			) {
				delete filterParams.priceMin
				delete filterParams.priceMax
			}
		}
	}

	// Marketplace
	if (props.filterState.marketplace?.length > 0) {
		const marketplaceValues = props.filterState.marketplace.map(
			(value: string) =>
				MERCARI_CONSTANTS.MARKETPLACE_MAP[
					value as keyof typeof MERCARI_CONSTANTS.MARKETPLACE_MAP
				] || value,
		)
		filterParams.marketplace = marketplaceValues.join(',')
	}

	// Sort
	if (props.filterState.sort) {
		if (props.filterState.sort === 'price_low') {
			filterParams.sort = 'price'
			filterParams.order = 'asc'
		} else if (props.filterState.sort === 'price_high') {
			filterParams.sort = 'price'
			filterParams.order = 'desc'
		} else if (props.filterState.sort === 'newest') {
			filterParams.sort = 'created_time'
			filterParams.order = 'desc'
		}
	}

	return filterParams
}

/**
 * Ensures sufficient products are available for display by using remaining products first,
 * then fetching additional products if needed.
 *
 * @description This function implements a two-scenario approach:
 * - Scenario 1: If remaining products are sufficient, use them directly
 * - Scenario 2: If insufficient, fetch more products through API calls with retry logic
 *
 * The function handles:
 * - Duplicate product filtering
 * - Excess product storage for future use
 * - Retry mechanism with consecutive empty result limits
 * - State management for pagination
 *
 * @param {any} initialData - Initial data containing products, hasNext, searchKeyword, totalCount
 * @param {Record<string, any>} filterParams - Filter parameters for API requests
 * @param {Record<string, any>} extraParams - Additional parameters for API requests
 * @param {number} targetCount - Target number of products to fetch (defaults to ITEMS_PER_PAGE)
 * @param {boolean} hasNext - External indication of whether more data is available
 *
 * @returns {Promise<{
 *   products: IMercariProductListItem[],
 *   page: number,
 *   totalCount: number,
 *   hasNext: boolean,
 *   searchKeyword: string
 * }>} Object containing accumulated products and pagination info
 *
 * @throws {Error} When API requests fail after maximum retry attempts
 */
const ensureSufficientProducts = async (
	initialData: any,
	filterParams: Record<string, any>,
	extraParams: Record<string, any>,
	targetCount: number = ITEMS_PER_PAGE.value,
	hasNext: boolean = false,
) => {
	let accumulatedProducts = [...state.remainingProducts]

	if (accumulatedProducts.length >= targetCount) {
		state.remainingProducts = accumulatedProducts.slice(targetCount)
		accumulatedProducts = accumulatedProducts.slice(0, targetCount)

		return {
			products: accumulatedProducts,
			page: state.page,
			totalCount: initialData.totalCount || 0,
			hasNext: state.remainingProducts.length > 0 || hasNext,
			searchKeyword: initialData.searchKeyword,
		}
	}

	state.remainingProducts = []

	let currentFetchPage = state.page
	let hasMoreData = initialData.hasNext
	let finalSearchKeyword = initialData.searchKeyword
	let fetchAttempts = 0
	const maxFetchAttempts = 10
	let consecutiveEmptyResults = 0
	const maxConsecutiveEmptyResults = 3

	// eslint-disable-next-line no-await-in-loop
	while (
		accumulatedProducts.length < targetCount &&
		hasMoreData &&
		fetchAttempts < maxFetchAttempts &&
		consecutiveEmptyResults < maxConsecutiveEmptyResults
	) {
		let shouldContinue = false

		try {
			fetchAttempts += 1

			let tempData

			if (currentFetchPage === state.page && initialData.products.length > 0) {
				tempData = initialData
			} else {
				// eslint-disable-next-line no-await-in-loop
				tempData = await fetchMercariProductList({
					keyword: props.keyword,
					page: currentFetchPage,
					limit: ITEMS_PER_PAGE.value,
					...filterParams,
					...extraParams,
				})

				tempData.products.forEach(product => {
					product.isBuyaholicProduct = false
				})
			}

			const newFilteredProducts = filterDuplicateProducts(tempData.products)

			if (newFilteredProducts.length === 0) {
				consecutiveEmptyResults += 1

				hasMoreData = tempData.hasNext
				if (hasMoreData) {
					currentFetchPage += 1
					shouldContinue = true
				} else {
					break
				}
			}

			if (!shouldContinue && newFilteredProducts.length > 0) {
				consecutiveEmptyResults = 0

				const remainingCount = targetCount - accumulatedProducts.length
				const productsToAdd = newFilteredProducts.slice(0, remainingCount)
				const excessProducts = newFilteredProducts.slice(remainingCount)

				state.remainingProducts = [
					...state.remainingProducts,
					...excessProducts,
				]
				accumulatedProducts = [...accumulatedProducts, ...productsToAdd]

				currentFetchPage += 1
				hasMoreData = tempData.hasNext
				finalSearchKeyword = tempData.searchKeyword || finalSearchKeyword

				if (accumulatedProducts.length >= targetCount) {
					break
				}
			}
		} catch (error) {
			console.error(
				`❌ Failed attempt ${fetchAttempts} to fetch more products:`,
				error,
			)

			if (fetchAttempts < maxFetchAttempts) {
				currentFetchPage += 1
				shouldContinue = true
			} else {
				console.error('🛑 Reached maximum fetch attempts, stopping backfill')
				break
			}
		}
	}

	state.page = currentFetchPage

	return {
		products: accumulatedProducts,
		page: currentFetchPage,
		totalCount: initialData.totalCount || 0,
		hasNext: hasMoreData || state.remainingProducts.length > 0,
		searchKeyword: finalSearchKeyword,
	}
}

const fetchProducts = async (extraParams = {}, isLoadMore = false) => {
	state.status = API_STATUS.LOADING

	if ('searchLang' in extraParams) {
		if (extraParams.searchLang === undefined) {
			delete extraParams.searchLang
		}
	} else if (props.isTranslated) {
		extraParams = { ...extraParams, searchLang: 'native' }
	}

	try {
		let data
		let isInLoadMore = false

		if (isBuyaholicMode.value) {
			try {
				let apiData: any = []
				let apiEndpoint = ''
				const params = new URLSearchParams()

				switch (props.type) {
					case 'buyaholic-filter':
						if (props.warehouses) params.append('warehouses', props.warehouses)
						if (props.interests) params.append('interests', props.interests)
						if (props.sort) params.append('sort', props.sort)
						params.append('page', state.page.toString())
						params.append('limit', ITEMS_PER_PAGE.value.toString())
						apiEndpoint = `/posts/discover?${params.toString()}`
						break

					case 'buyaholic-search':
						apiEndpoint = `/search?q=${encodeURIComponent(
							props.keyword,
						)}&limit=${ITEMS_PER_PAGE.value}&start=${state.page + 1}`
						break

					case 'buyaholic-user':
						apiEndpoint = `/posts?username=${encodeURIComponent(
							props.username,
						)}&limit=${ITEMS_PER_PAGE.value}&start=${state.page + 1}`
						break

					default:
						apiEndpoint = `/search?q=&limit=${ITEMS_PER_PAGE.value}&start=${
							state.page + 1
						}`
						break
				}

				const response = await instanceProductSharing.get(apiEndpoint)

				apiData = response.data.data || []
				const meta = response.data.meta || {}

				data = {
					products: formatBuyaholicProducts(apiData),
					hasNext: meta.has_next_page || meta.total_count > (meta.count || 0),
				}
			} catch (error) {
				let backupKeyword = ''
				if (props.type === 'buyaholic-search') {
					backupKeyword = props.keyword
				} else if (props.type === 'buyaholic-user') {
					backupKeyword = props.username
				} else {
					backupKeyword = props.interests || props.warehouses || ''
				}

				data = await fetchMercariProductList({
					keyword: backupKeyword,
					page: state.page,
					limit: ITEMS_PER_PAGE.value,
				})

				// Buyaholic fallback uses mercari data, needs to set flag and deduplicate
				data.products.forEach(product => {
					product.isBuyaholicProduct = false
				})

				// Filter duplicate products
				data.products = filterDuplicateProducts(data.products)
			}
		} else if (isShopMode.value) {
			const filterParams = buildFilterParams()
			data = await fetchMercariProductList({
				shop_id: props.shopId,
				page: state.page,
				limit: ITEMS_PER_PAGE.value,
				...filterParams,
				...extraParams,
			})

			data.products.forEach(product => {
				product.isBuyaholicProduct = false
			})

			// Only apply backfill logic during Load More
			if (isLoadMore) {
				isInLoadMore = true
				data = await ensureSufficientProducts(
					data,
					filterParams,
					extraParams,
					ITEMS_PER_PAGE.value,
					state.hasNextPage,
				)
			}
		} else {
			const filterParams = buildFilterParams()
			data = await fetchMercariProductList({
				keyword: props.keyword,
				page: state.page,
				limit: ITEMS_PER_PAGE.value,
				...filterParams,
				...extraParams,
			})

			data.products.forEach((product: IMercariProductListItem) => {
				product.isBuyaholicProduct = false
			})

			// Only apply backfill logic during Load More
			if (isLoadMore) {
				isInLoadMore = true
				data = await ensureSufficientProducts(
					data,
					filterParams,
					extraParams,
					ITEMS_PER_PAGE.value,
					state.hasNextPage,
				)
			}
		}

		state.productList = [...state.productList, ...data.products]
		state.hasNextPage = data.hasNext
		if (!isInLoadMore) {
			state.page += 1
		}
		state.status = API_STATUS.SUCCESS

		emitFetchComplete(true, data.products, data.searchKeyword)

		createGAEvent('view_item_list', {
			page_name: props.locationId,
			items: handleGAViewItemListEvent(data.products, props.keyword),
			keyword_translation: props.isTranslated ? 'On' : 'Off',
			sorting_method: getSortingMethodLabel(
				props.filterState?.sort || 'recommended',
			),
			...(props.filterState ? getCategoryLevels(props.filterState) : {}),
			...(props.filterState ? getFilterTrackingValues(props.filterState) : {}),
		})

		state.lastFetchedIndex = state.productList.length
	} catch (error) {
		console.error('Failed to fetch products:', error)
		state.status = API_STATUS.ERROR
		emitFetchComplete(false, [])
	}
}

async function fetchSettingData() {
	try {
		const [warehousesData, currenciesData, ratesData] = await Promise.all([
			fetchSettingWarehouses(),
			fetchSettingSupportedCurrencies(),
			fetchSettingInsurance(),
		])

		warehouseIdMap.value = warehousesData
		supportedCurrencies.value = currenciesData
		rates.value = ratesData

		return true
	} catch (error) {
		console.error('fetch exchangeLocalPrice error:', error)
		return false
	}
}

function getCurrencySymbol(warehouse: string): string {
	if (
		!supportedCurrencies.value ||
		!warehouseIdMap.value ||
		!warehouseIdMap.value[warehouse]
	) {
		return ''
	}

	return getCurrencySymbolOriginal({
		supportedCurrencies: supportedCurrencies.value,
		warehouseIdMap: warehouseIdMap.value,
		warehouse,
	})
}

function calculateLocalPrice(price: number, warehouse: string): string {
	if (!rates.value || !warehouse || !price) {
		return ''
	}

	return exchangeLocalPrice({
		rates: rates.value,
		price,
		warehouse,
		count: 1,
		floatMin: 0,
		floatMax: 0,
	})
}

function formatBuyaholicProducts(apiData: any[]): IMercariProductListItem[] {
	return apiData.map((item: any) => {
		let imageUrl = ''

		if (item.media && item.media.length > 0) {
			if (item.media[0].type === 'ps_video') {
				imageUrl = item.media[0].preview || ''
			} else {
				imageUrl = item.media[0].cover || ''
			}
		}

		const price = item.item_unit_price ? parseFloat(item.item_unit_price) : null
		const warehouse = item.warehouse || ''

		const localPrice =
			price && warehouse ? calculateLocalPrice(price, warehouse) : ''
		const currencySymbol = warehouse ? getCurrencySymbol(warehouse) : ''

		return {
			id: item.id,
			name: item.title || '',
			originalPrice: price,
			price,
			currency: item.currency || null,
			warehouse,
			discountPercentage: item.discounted,
			images: imageUrl ? [imageUrl] : [''],
			status: 'on_sale',
			url: item.item_url || '',
			hasPromotions: false,
			isBuyaholicProduct: true,
			localPrice,
			currencySymbol,
			can_express_checkout: item.can_express_checkout,
		}
	})
}

function getItemListName(
	url: string,
	locationId: string,
	isBuyaholic: boolean,
) {
	if (isBuyaholic) {
		return 'Community Post Listing'
	}

	if (locationId === GaGeneralPageName.MercariCampaign) {
		return 'Product Listing-Campaign'
	}

	return url.includes('search')
		? 'Product Listing'
		: 'Product Listing-Recommendation'
}

function getItemCategory(product: IMercariProductListItem): string {
	if (!product.isBuyaholicProduct) {
		return 'Mercari'
	}

	return product.can_express_checkout ? 'Express Checkout' : 'Normal Request'
}

function getItemId(product: IMercariProductListItem, isBuyaholic: boolean) {
	return isBuyaholic ? `post_${product.id}` : `product_${product.id}`
}

function transformProductData(
	products: IMercariProductListItem[],
	searchTerm: string,
	listName: string,
) {
	const currentIndex = state.lastFetchedIndex ? state.lastFetchedIndex : 0

	const result = products.map((product, index) => ({
		location_id: props.locationId,
		item_list_name: listName,
		item_id: getItemId(product, isBuyaholicMode.value),
		item_name: product.name,
		item_category: getItemCategory(product),
		index: currentIndex + index + 1,
		keyword: searchTerm,
	}))
	return result
}

function handleGAViewItemListEvent(
	products: IMercariProductListItem[],
	searchKeyword: string,
) {
	const listName = getItemListName(
		window.location.pathname,
		props.locationId,
		isBuyaholicMode.value,
	)

	const formattedKeyword = formatTrackingKeyword(
		isShopMode.value,
		props.shopId,
		searchKeyword,
	)

	const transformedData = transformProductData(
		products,
		formattedKeyword,
		listName,
	)

	return transformedData
}

onMounted(() => {
	Promise.allSettled([fetchSettingData(), fetchProducts()]).then(() => {})
})

defineExpose({
	fetchProducts,
	state,
})
</script>

<template>
	<div class="flex flex-col items-center text-left">
		<!-- Product List with Loading States -->
		<template v-if="currentDisplay === 'LOADING' || currentDisplay === 'LIST'">
			<div
				class="grid w-full grid-cols-2 gap-strong-friends gap-x-16px gap-y-8px mdp:grid-cols-3 mdp:gap-y-close-ish-friends mdl:grid-cols-4 mdl:gap-x-20px lg:grid-cols-5"
			>
				<!-- Existing Products -->
				<template v-if="state.productList.length > 0">
					<MercariProductListItem
						v-for="product in state.productList"
						:key="product.id"
						:item="product"
						:item-content="itemContent"
						@item-click="handleItemClick"
					/>
				</template>

				<!-- Loading Skeleton (Initial or LoadMore) -->
				<template v-if="currentDisplay === 'LOADING' || isLoadingMore">
					<MercariProductListItem
						v-for="i in ITEMS_PER_PAGE"
						:key="`loading-${i}`"
						:is-loading="true"
					/>
				</template>
			</div>

			<!-- View More Button: check if there is a custom slot first -->
			<slot
				name="viewMoreSlot"
				:keyword="keyword"
				:hasNextPage="state.hasNextPage"
			>
				<!-- default view more button -->
				<a
					:href="currentViewMoreLink"
					@click.prevent="handleViewMoreClick"
					class="mx-auto w-full mdp:w-[300px]"
				>
					<BaseButton
						v-if="shouldShowViewMoreButton"
						variant="secondary-orange"
						class="mt-16px w-full mdp:mt-casual-friends mdp:w-[300px]"
					>
						{{ loadMoreButtonText }}
					</BaseButton>
				</a>
			</slot>
		</template>

		<!-- Error State -->
		<template v-else-if="currentDisplay === 'ERROR'">
			<div
				class="mx-auto my-[120px] flex max-w-[300px] flex-col items-center gap-32px mdp:my-[140px]"
			>
				<div class="h-[150px] w-[150px]">
					<img
						class="w-full"
						:srcset="`
						${getVueAssetImagePath('mercari-product-list-server-error.png')}
					`"
						alt="mercari product list server-error"
					/>
				</div>

				<div class="flex flex-col items-center gap-16px">
					<div class="flex flex-col gap-12px">
						<p class="typography-h4-str text-center text-n-950">
							{{ props.edgeCase.serverError.title }}
						</p>
						<p class="typography-s1-reg text-center text-n-400">
							{{ props.edgeCase.serverError.description }}
						</p>
					</div>

					<div class="w-auto">
						<BaseButton
							variant="primary"
							size="medium"
							@click="fetchProducts()"
						>
							{{ props.edgeCase.serverError.buttonText }}
						</BaseButton>
					</div>
				</div>
			</div>
		</template>

		<!-- Empty State -->
		<template v-else-if="currentDisplay === 'EMPTY'">
			<div
				class="mx-auto my-[120px] flex max-w-[300px] flex-col items-center gap-32px mdp:my-[140px]"
			>
				<div class="h-[150px] w-[150px]">
					<img
						class="w-full"
						:srcset="`
						${getVueAssetImagePath('mercari-product-list-no-result.png')}
					`"
						alt="mercari product list no result"
					/>
				</div>

				<div class="flex flex-col items-center gap-16px">
					<div class="flex flex-col gap-12px">
						<p
							v-if="props.edgeCase.empty.title"
							class="typography-h4-str text-center text-n-950"
						>
							{{ props.edgeCase.empty.title }}
						</p>
						<p class="typography-s1-reg text-center text-n-400">
							{{ props.edgeCase.empty.description }}
						</p>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>
